import React, { useEffect, useState } from "react";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import { useTranslation } from "react-i18next";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import SelectInput from "../../components/forms/SelectInput";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";

const narrowShellStyle = {
  width: "100%",
  maxWidth: "720px",
  margin: "0 auto",
};

export default function DriverAddOfflineTransactions() {
  const { t } = useTranslation();
  const [vendorWallets, setVendorWallets] = useState([]);
  const [selectedVendorWalletId, setSelectedVendorWalletId] = useState("");
  const [printedTicketCount, setPrintedTicketCount] = useState("");
  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchVendorWallets();
  }, []);

  const fetchVendorWallets = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/driver/available-wallets`);
      setVendorWallets(res.data);
    } catch {
      console.error("❌ " + t("driver_add_offline_transactions.messages.fetch_wallets_failed"));
    }
  };

  const handleSubmitPrintedTickets = async () => {
    if (!selectedVendorWalletId || !printedTicketCount) {
      setMessage(t("driver_add_offline_transactions.messages.missing_inputs"));
      setMessageVariant("error");
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/driver/request-force-pay`, {
        from_wallet_id: Number(selectedVendorWalletId),
        delta_tickets_from_wallet: 0,
        delta_money_from_wallet: 0,
        to_wallet_id: Number(selectedVendorWalletId),
        delta_tickets_to_wallet: Number(printedTicketCount),
        delta_money_to_wallet: 0,
        header: "add-offline-traffic-request",
      });

      setMessage(t("driver_add_offline_transactions.messages.success"));
      setMessageVariant("success");
    } catch {
      setMessage(t("driver_add_offline_transactions.messages.failure"));
      setMessageVariant("error");
    }
  };

  return (
    <StandardScreen
      topNav={<TopNav title={t("driver_add_offline_transactions.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card title={t("driver_add_offline_transactions.title")}>
              <Stack gap="3">
                <FormField label={t("driver_add_offline_transactions.fields.wallet.label")}>
                  <SelectInput
                    value={selectedVendorWalletId}
                    onChange={(e) => {
                      setSelectedVendorWalletId(e.target.value);
                      setMessage("");
                    }}
                  >
                    <option value="">
                      {t("driver_add_offline_transactions.fields.wallet.placeholder")}
                    </option>

                      {vendorWallets
                        .filter((wallet) => wallet?.is_route_money_box)
                        .map((wallet) => (
                          <option key={wallet.wallet_id} value={wallet.wallet_id}>
                            {[
                              wallet.route_name,
                              wallet.start_point && wallet.end_point
                                ? `${wallet.start_point} → ${wallet.end_point}`
                                : null,
                              `${t("driver_add_offline_transactions.dropdown_item.ticket_balance")}: ${wallet.ticket_balance ?? 0}`,
                              `#${wallet.wallet_id}`,
                            ]
                              .filter(Boolean)
                              .join(" | ")}
                          </option>
                        ))}



                  </SelectInput>
                </FormField>

                <FormField label={t("driver_add_offline_transactions.fields.ticket_count.label")}>
                  <TextInput
                    type="number"
                    min="1"
                    value={printedTicketCount}
                    onChange={(e) => {
                      setPrintedTicketCount(e.target.value);
                      setMessage("");
                    }}
                    placeholder={t("driver_add_offline_transactions.fields.ticket_count.placeholder")}
                  />
                </FormField>

                <Button
                  onClick={handleSubmitPrintedTickets}
                  disabled={!selectedVendorWalletId || !printedTicketCount}
                  fullWidth
                >
                  {t("driver_add_offline_transactions.actions.submit")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />
              </Stack>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}